import { useState, useEffect } from 'react';
import { tenantApi } from '../services/api';

interface Tenant {
  id: string;
  name: string;
  domain: string;
  azureTenantId: string;
  azureClientId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function Tenants() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [adminEmail, setAdminEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    azureTenantId: '',
    azureClientId: '',
    azureClientSecret: '',
  });

  const handleSendConsentLink = async () => {
    if (!adminEmail || !adminEmail.includes('@')) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setSendingEmail(true);
    setEmailError('');
    
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/auth/send-consent-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ adminEmail, tenantId: selectedTenantId })
      });

      if (response.ok) {
        setEmailSent(true);
        setTimeout(() => {
          setShowEmailModal(false);
          setEmailSent(false);
          setAdminEmail('');
          setSelectedTenantId(null);
        }, 3000);
      } else {
        const data = await response.json();
        setEmailError(data.error || 'Failed to send email');
      }
    } catch (err) {
      setEmailError('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const response = await tenantApi.getAll();
      setTenants(response.data);
    } catch (error) {
      console.error('Failed to load tenants:', error);
      alert('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        // Update existing tenant
        await tenantApi.update(editingId, {
          name: formData.name,
          azureClientSecret: formData.azureClientSecret || undefined,
        });
        alert('Tenant updated successfully!');
      } else {
        // Create new tenant
        await tenantApi.create(formData);
        alert('Tenant created successfully!');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({
        name: '',
        domain: '',
        azureTenantId: '',
        azureClientId: '',
        azureClientSecret: '',
      });
      loadTenants();
    } catch (error) {
      console.error('Failed to save tenant:', error);
      alert('Failed to save tenant');
    }
  };

  const handleEdit = (tenant: Tenant) => {
    setEditingId(tenant.id);
    setFormData({
      name: tenant.name,
      domain: tenant.domain,
      azureTenantId: tenant.azureTenantId,
      azureClientId: tenant.azureClientId,
      azureClientSecret: '', // Don't pre-fill password
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete tenant "${name}"?`)) return;
    try {
      await tenantApi.delete(id);
      alert('Tenant deleted!');
      loadTenants();
    } catch (error) {
      console.error('Failed to delete tenant:', error);
      alert('Failed to delete tenant');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Tenants / Clients</h2>
        <button
          onClick={() => {
            setShowForm(!showForm);
            if (showForm) {
              setEditingId(null);
              setFormData({
                name: '',
                domain: '',
                azureTenantId: '',
                azureClientId: '',
                azureClientSecret: '',
              });
            }
          }}
          style={{
            padding: '10px 20px',
            backgroundColor: '#0066cc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          {showForm ? 'Cancel' : '+ Add Tenant'}
        </button>
      </div>

      {showForm && (
        <div style={{
          border: '1px solid #ddd',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px',
          backgroundColor: '#f9f9f9',
        }}>
          <h3>{editingId ? 'Edit Tenant' : 'Create New Tenant'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Company Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
              />
            </div>

            {!editingId && (
              <>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Domain *</label>
                  <input
                    type="text"
                    required
                    placeholder="company.com"
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Azure Tenant ID *</label>
                  <input
                    type="text"
                    required
                    placeholder="12345678-1234-1234-1234-123456789012"
                    value={formData.azureTenantId}
                    onChange={(e) => setFormData({ ...formData, azureTenantId: e.target.value })}
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Azure Client ID *</label>
                  <input
                    type="text"
                    required
                    placeholder="87654321-4321-4321-4321-210987654321"
                    value={formData.azureClientId}
                    onChange={(e) => setFormData({ ...formData, azureClientId: e.target.value })}
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  />
                </div>
              </>
            )}

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>
                Azure Client Secret {editingId ? '(leave blank to keep current)' : '*'}
              </label>
              <input
                type="password"
                required={!editingId}
                value={formData.azureClientSecret}
                onChange={(e) => setFormData({ ...formData, azureClientSecret: e.target.value })}
                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
              />
              <small style={{ color: '#666' }}>
                {editingId 
                  ? 'Only enter a new secret if you want to update it' 
                  : 'This will be encrypted and stored securely'
                }
              </small>
            </div>

            <button
              type="submit"
              style={{
                padding: '10px 20px',
                backgroundColor: '#0066cc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              {editingId ? 'Update Tenant' : 'Create Tenant'}
            </button>
          </form>
        </div>
      )}

      <div>
        <h3>Existing Tenants ({tenants.length})</h3>
        {tenants.length === 0 ? (
          <p style={{ color: '#666' }}>No tenants yet. Click "Add Tenant" to create one.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                <th style={{ padding: '10px' }}>Name</th>
                <th style={{ padding: '10px' }}>Domain</th>
                <th style={{ padding: '10px' }}>Azure Tenant ID</th>
                <th style={{ padding: '10px' }}>Status</th>
                <th style={{ padding: '10px' }}>Created</th>
                <th style={{ padding: '10px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <tr key={tenant.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '10px' }}><strong>{tenant.name}</strong></td>
                  <td style={{ padding: '10px' }}>{tenant.domain}</td>
                  <td style={{ padding: '10px', fontSize: '12px', fontFamily: 'monospace' }}>
                    {tenant.azureTenantId}
                  </td>
                  <td style={{ padding: '10px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: tenant.isActive ? '#e6f4ea' : '#fce8e6',
                      color: tenant.isActive ? '#1e7e34' : '#c5221f',
                    }}>
                      {tenant.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '10px', fontSize: '12px' }}>
                    {new Date(tenant.createdAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '10px' }}>
                    <button
                      onClick={() => handleEdit(tenant)}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: '#0066cc',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        marginRight: '5px',
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTenantId(tenant.id);
                        setShowEmailModal(true);
                      }}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        marginRight: '5px',
                      }}
                    >
                      Send Consent
                    </button>
                    <button
                      onClick={() => handleDelete(tenant.id, tenant.name)}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div
          onClick={() => setShowEmailModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '90%',
            }}
          >
            {emailSent ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>✓</div>
                <h3 style={{ margin: 0, color: '#1f2937' }}>Email Sent!</h3>
                <p style={{ color: '#6b7280', marginTop: '10px' }}>
                  Consent link sent to {adminEmail}
                </p>
              </div>
            ) : (
              <>
                <h3 style={{ margin: '0 0 20px', color: '#1f2937' }}>Send Consent Link</h3>
                <p style={{ color: '#6b7280', marginBottom: '20px' }}>
                  Enter the Global Admin email address
                </p>
                <input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => {
                    setAdminEmail(e.target.value);
                    setEmailError('');
                  }}
                  placeholder="admin@company.com"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: emailError ? '1px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '16px',
                    marginBottom: '10px',
                  }}
                />
                {emailError && (
                  <p style={{ color: '#ef4444', fontSize: '14px', margin: '0 0 20px' }}>{emailError}</p>
                )}
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button
                    onClick={handleSendConsentLink}
                    disabled={sendingEmail}
                    style={{
                      flex: 1,
                      background: sendingEmail ? '#9ca3af' : '#667eea',
                      color: 'white',
                      padding: '12px',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: sendingEmail ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {sendingEmail ? 'Sending...' : 'Send Email'}
                  </button>
                  <button
                    onClick={() => setShowEmailModal(false)}
                    style={{
                      padding: '12px 24px',
                      background: 'transparent',
                      color: '#6b7280',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
